import { Plus, Trash2, Loader2, X, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { PropertyTemplate } from "@/types/property";
import { RoomMixItem } from "@/lib/kpis";
import { updateProperty } from "@/lib/services/propertyService";

type Props = {
  dealId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (savedRoomMix: RoomMixItem[], totalRooms: number) => void;
  roomMix: RoomMixItem[];
  property: PropertyTemplate;
};

const MAX_ROOM_TYPES = 5;

export default function RoomMixModalAdmin({
  dealId,
  isOpen,
  onClose,
  onSaved,
  roomMix,
  property,
}: Props) {
  const [localRoomMix, setLocalRoomMix] = useState<RoomMixItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setLocalRoomMix(roomMix.length > 0 ? [...roomMix] : []);
      setErrors({});
    }
  }, [isOpen]);

  const validateRoomMix = () => {
    const newErrors: Record<string, string> = {};

    localRoomMix.forEach((item, index) => {
      if (!item.name?.trim()) {
        newErrors[`name-${index}`] = "Room type name is required";
      }
      if (!item.rooms || item.rooms <= 0) {
        newErrors[`rooms-${index}`] = "Number of rooms must be greater than 0";
      }
      if (item.adrWeight && (item.adrWeight < 0 || item.adrWeight > 1000)) {
        newErrors[`adrWeight-${index}`] =
          "ADR weight must be between 0 and 1000";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddRoomType = () => {
    if (localRoomMix.length >= MAX_ROOM_TYPES) return;

    setLocalRoomMix([
      ...localRoomMix,
      {
        id: crypto.randomUUID(),
        name: "",
        rooms: 0,
        adrWeight: 100,
      },
    ]);
  };

  const handleRemoveRoomType = (index: number) => {
    if (localRoomMix.length <= 1) return;
    setLocalRoomMix(localRoomMix.filter((_, i) => i !== index));
  };

  const handleChange = (
    index: number,
    field: keyof RoomMixItem,
    value: string | number,
  ) => {
    setLocalRoomMix((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: field === "name" ? String(value) : Number(value),
      };
      return next;
    });

    if (errors[`${field}-${index}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`${field}-${index}`];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!validateRoomMix()) return;

    setSaving(true);
    try {
      const filtered = localRoomMix.filter((item) => item.name?.trim() !== "");
      const totalRoomsCount = filtered.reduce(
        (sum, item) => sum + (item.rooms || 0),
        0,
      );

      if (dealId) {
        const updatedProperty = {
          ...property,
          kpis: {
            ...property.kpis,
            roomMix: filtered,
            rooms: { total: totalRoomsCount },
          },
          rooms_total: totalRoomsCount,
        };
        await updateProperty(dealId, updatedProperty);
      }

      onSaved(filtered, totalRoomsCount);
    } catch (error) {
      console.error("Failed to save room mix:", error);
    } finally {
      setSaving(false);
    }
  };
  const totalRooms = localRoomMix.reduce(
    (sum, item) => sum + (item.rooms || 0),
    0,
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <span>Room Mix Configuration</span>
            {localRoomMix.length > 0 && (
              <span className="text-sm font-normal text-slate-500">
                ({localRoomMix.length} of {MAX_ROOM_TYPES} types)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-auto">
          {localRoomMix.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <p className="text-slate-500 mb-4">No room types configured</p>
              <Button
                className="bg-primary-blue"
                onClick={handleAddRoomType}
                disabled={localRoomMix.length >= MAX_ROOM_TYPES}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Room Type
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[300px]">Room Type Name</TableHead>
                    <TableHead className="w-[150px]">Number of Rooms</TableHead>
                    {/* <TableHead className="w-[150px]">ADR Weight (%)</TableHead> */}
                    <TableHead className="w-[80px] text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {localRoomMix.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <Input
                            value={item.name}
                            onChange={(e) =>
                              handleChange(index, "name", e.target.value)
                            }
                            placeholder="e.g., Standard"
                            className={
                              errors[`name-${index}`] ? "border-red-500" : ""
                            }
                          />
                          {errors[`name-${index}`] && (
                            <p className="text-xs text-red-500">
                              {errors[`name-${index}`]}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Input
                            type="number"
                            min="0"
                            value={item.rooms || ""}
                            onChange={(e) =>
                              handleChange(index, "rooms", e.target.value)
                            }
                            placeholder="0"
                            className={
                              errors[`rooms-${index}`] ? "border-red-500" : ""
                            }
                          />
                          {errors[`rooms-${index}`] && (
                            <p className="text-xs text-red-500">
                              {errors[`rooms-${index}`]}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      {/* <TableCell>
                        <div className="space-y-1">
                          <Input
                            type="number"
                            min="0"
                            max="1000"
                            value={item.adrWeight ?? ""}
                            onChange={(e) =>
                              handleChange(index, "adrWeight", e.target.value)
                            }
                            placeholder="100"
                            className={
                              errors[`adrWeight-${index}`]
                                ? "border-red-500"
                                : ""
                            }
                          />
                          {errors[`adrWeight-${index}`] && (
                            <p className="text-xs text-red-500">
                              {errors[`adrWeight-${index}`]}
                            </p>
                          )}
                        </div>
                      </TableCell> */}
                      <TableCell className="text-right">
                        {localRoomMix.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveRoomType(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {localRoomMix.length < MAX_ROOM_TYPES && (
                <Button
                  variant="outline"
                  onClick={handleAddRoomType}
                  className="w-full border-dashed"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Room Type
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">Total Rooms</p>
              <p className="text-xs text-slate-500">Sum of all room types</p>
            </div>
            <div className="text-2xl font-bold text-slate-900">
              {totalRooms}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || localRoomMix.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
